import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Languages } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/config';
import { cn } from '@/lib/utils';

export type PdfDevelopmentsLimit = 3 | 5 | null;

interface CustomizeFilenameDialogProps {
  open: boolean;
  onClose: () => void;
  onExport: (
    customFilename: string,
    language: SupportedLanguage,
    developmentsLimit?: PdfDevelopmentsLimit
  ) => void;
  projectCode?: string | null;
  meetingNumber?: number;
  meetingDate?: string;
  isMultipleMeetings?: boolean;
  currentLanguage?: SupportedLanguage;
  showDevelopmentsLimitOption?: boolean;
  defaultDevelopmentsLimit?: PdfDevelopmentsLimit;
}

export function CustomizeFilenameDialog({
  open,
  onClose,
  onExport,
  projectCode,
  meetingNumber,
  meetingDate,
  isMultipleMeetings = false,
  currentLanguage = 'pt-BR',
  showDevelopmentsLimitOption = false,
  defaultDevelopmentsLimit = null,
}: CustomizeFilenameDialogProps) {
  const { t } = useTranslation('common');
  const [filename, setFilename] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(currentLanguage);
  const [developmentsLimit, setDevelopmentsLimit] = useState<PdfDevelopmentsLimit>(defaultDevelopmentsLimit);

  useEffect(() => {
    if (open) {
      setFilename(generateDefaultFilename());
      setSelectedLanguage(currentLanguage);
      setDevelopmentsLimit(defaultDevelopmentsLimit);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentLanguage, defaultDevelopmentsLimit]);

  const generateDefaultFilename = () => {
    // Data no formato AAMMDD (ano com 2 dígitos)
    const date = meetingDate ? meetingDate.slice(2).replace(/-/g, '') : '';

    if (isMultipleMeetings) {
      return projectCode ? `${projectCode}_TODAS_AS_ATAS` : 'TODAS_AS_ATAS';
    } else {
      const code = projectCode || 'PROJETO';
      const number = meetingNumber ? `_Nº${meetingNumber}` : '';
      return `${code}_${date}_ATA_REUNIAO${number}`;
    }
  };

  const handleExport = () => {
    onExport(
      filename.trim() || generateDefaultFilename(),
      selectedLanguage,
      showDevelopmentsLimitOption ? developmentsLimit : undefined
    );
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleExport();
    }
  };

  const willTranslate = selectedLanguage !== currentLanguage;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {t('pdf_export.title', 'Exportar PDF')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('pdf_export.description', 'Configure o nome do arquivo e o idioma do documento')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label htmlFor="filename" className="text-sm font-medium">
              {t('pdf_export.filename_label', 'Nome do arquivo')}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={generateDefaultFilename()}
                maxLength={100}
                autoFocus
                className="font-mono text-sm"
              />
              <span className="text-sm text-muted-foreground">.pdf</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Languages className="h-4 w-4 text-muted-foreground" />
              {t('pdf_export.language_label', 'Idioma do documento')}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.entries(SUPPORTED_LANGUAGES) as [SupportedLanguage, typeof SUPPORTED_LANGUAGES[SupportedLanguage]][]).map(
                ([code, info]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setSelectedLanguage(code)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors',
                      selectedLanguage === code
                        ? 'border-primary bg-primary/5 text-primary font-medium'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50 text-foreground'
                    )}
                  >
                    <span className="text-base leading-none">{info.flag}</span>
                    <span className="truncate">{info.nativeName}</span>
                    {selectedLanguage === code && (
                      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                    )}
                  </button>
                )
              )}
            </div>

            {willTranslate && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
                <span className="text-sm leading-none mt-0.5">⚡</span>
                <span>
                  {t(
                    'pdf_export.translation_notice',
                    'O conteúdo do documento será traduzido automaticamente para o idioma selecionado.'
                  )}
                </span>
              </p>
            )}
          </div>

          {showDevelopmentsLimitOption && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t(
                  'pdf_export.developments_limit_label',
                  'Desenvolvimentos por item no PDF'
                )}
              </Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setDevelopmentsLimit(3)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm transition-colors',
                    developmentsLimit === 3
                      ? 'border-primary bg-primary/5 text-primary font-medium'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50 text-foreground'
                  )}
                >
                  3
                </button>
                <button
                  type="button"
                  onClick={() => setDevelopmentsLimit(5)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm transition-colors',
                    developmentsLimit === 5
                      ? 'border-primary bg-primary/5 text-primary font-medium'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50 text-foreground'
                  )}
                >
                  5
                </button>
                <button
                  type="button"
                  onClick={() => setDevelopmentsLimit(null)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm transition-colors',
                    developmentsLimit === null
                      ? 'border-primary bg-primary/5 text-primary font-medium'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50 text-foreground'
                  )}
                >
                  {t('pdf_export.no_limit', 'Sem limite')}
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} size="sm">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleExport}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            {t('pdf_export.export_button', 'Exportar PDF')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
